<template>
  <component :is="shown" v-if="shown" />
</template>

<script setup lang="ts">
import { onMounted, shallowRef, type Component } from 'vue'
import { useRoute } from 'vue-router'

import { useEventBus } from '@/composables/event_bus'
import { useUserStore } from '@/stores/user'

import { AiSuggestionsNotification } from '@/components/misc/Notification/AiSuggestionsNotification'
import { ReleaseNotesNotification } from '@/components/misc/Notification/ReleaseNotesNotification'
import { ServicesNotification } from '@/components/misc/Notification/ServicesNotification'
import { useAiSuggestionsNotification } from '@/components/misc/Notification/composables/ai_suggestions_notification'
import { useReleaseNotesNotification } from '@/components/misc/Notification/composables/release_notes_notification'
import { useServicesNotification } from '@/components/misc/Notification/composables/services_notification'

interface Candidate {
  component: Component
  // Resolves to whether the popup has something to show.
  prepare: () => Promise<boolean>
}

const route = useRoute()
const eventBus = useEventBus()
const userStore = useUserStore()

// Evaluated once: at most one popup per app load, in priority order.
const candidates: Candidate[] = []
if (userStore.hasPermission(283)) {
  candidates.push({
    component: ServicesNotification,
    prepare: useServicesNotification().prepare,
  })
}
if (route.name !== 'ReleaseNotesView') {
  candidates.push({
    component: ReleaseNotesNotification,
    prepare: useReleaseNotesNotification().prepare,
  })
}
if (userStore.hasPermission(373)) {
  candidates.push({
    component: AiSuggestionsNotification,
    prepare: useAiSuggestionsNotification().prepare,
  })
}

// Read before the page drops the deep-link query from the URL.
const urlOpensModal = route.query.action === 'open'

const shown = shallowRef<Component | null>(null)

onMounted(async () => {
  // The popups wait for the next load rather than cover that modal.
  if (urlOpensModal) {
    return
  }
  // One by one: a prepare marks its content as seen, which must not happen
  // for popups after the one that is shown.
  for (const { component, prepare } of candidates) {
    try {
      if (await prepare()) {
        shown.value = component
        return
      }
    } catch (err) {
      eventBus.emit('error', err)
    }
  }
})
</script>
